// Distribution — Echo's domain, rebranded. Real list of clips with status filters,
// inline verify action for submissions, and platform-broken-down stats.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Clip } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';

const STATUSES: Array<{ id: string; label: string }> = [
  { id: '',          label: 'All' },
  { id: 'submitted', label: 'Under review' },
  { id: 'claimed',   label: 'In progress' },
  { id: 'verified',  label: 'Verified' },
  { id: 'rejected',  label: 'Rejected' },
];

// Human-readable status labels. "claimed" = a clipper reserved the slot but
// hasn't submitted the clip yet; "submitted" = clip is in and awaiting the
// operator's review. ("Claim" read as confusing — Kevin 2026-05-27.)
const STATUS_LABEL: Record<string, string> = {
  submitted: 'Under review',
  claimed: 'In progress',
  verified: 'Verified',
  rejected: 'Rejected',
  paid: 'Paid',
};
function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

function statusColor(status: string): string {
  switch (status) {
    case 'verified': return T.live;
    case 'claimed':
    case 'submitted': return T.warning;
    case 'paid': return T.live;
    case 'rejected': return T.error;
    default: return T.inkSoft;
  }
}

function platformBadge(platform: string | null): string {
  if (!platform) return '—';
  return platform;
}

export default function Distribution() {
  const [filter, setFilter] = useState<string>('submitted');
  // Fetch ALL clips once and filter client-side. Previously each tab refetched
  // only its own status, so the per-tab count badges (derived from the loaded
  // list) showed 0 for every tab except the active one. Holding the full set
  // lets every badge show its real total at all times.
  const [allClips, setAllClips] = useState<Clip[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    setError(null);
    return api.clips
      .list({ limit: 500 })
      .then((res) => setAllClips(res.clips))
      .catch((e: Error) => setError(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.clips
      .list({ limit: 500 })
      .then((res) => {
        if (!cancelled) setAllClips(res.clips);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-status totals across ALL clips — independent of the active tab.
  const counts = useMemo(() => {
    if (!allClips) return null;
    return allClips.reduce<Record<string, number>>((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    }, {});
  }, [allClips]);

  // The list shown for the active tab (client-side filtered).
  const clips = useMemo(
    () => (allClips == null ? null : filter ? allClips.filter((c) => c.status === filter) : allClips),
    [allClips, filter]
  );

  async function handleVerify(clipId: string, approve: boolean) {
    if (busyId) return;
    setBusyId(clipId);
    try {
      let views: number | undefined;
      if (approve) {
        const input = window.prompt('Verified view count?', '0');
        if (input === null) {
          setBusyId(null);
          return;
        }
        const n = parseInt(input, 10);
        if (!Number.isFinite(n) || n < 0) {
          alert('View count must be a non-negative integer.');
          setBusyId(null);
          return;
        }
        views = n;
      } else {
        const reason = window.prompt('Rejection reason?', '');
        if (reason === null) {
          setBusyId(null);
          return;
        }
        await api.clips.verify(clipId, { approve: false, rejection_reason: reason });
        await reload();
        setBusyId(null);
        return;
      }
      await api.clips.verify(clipId, { approve: true, views });
      await reload();
    } catch (err) {
      alert(`Verify failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Distribution"
        subtitle="Bounties, creators, view tracking, payouts. The clip distribution surface."
      />

      {/* Filter pills */}
      <div style={distStyles.filters}>
        {STATUSES.map((s) => {
          const active = filter === s.id;
          const count = counts && s.id ? counts[s.id] || 0 : null;
          return (
            <button
              key={s.id || 'all'}
              type="button"
              onClick={() => setFilter(s.id)}
              style={{
                ...distStyles.pill,
                ...(active ? distStyles.pillActive : {}),
              }}
            >
              {s.label}
              {count !== null && <span style={distStyles.pillCount}>{count}</span>}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={distStyles.empty}>
          <span style={{ color: T.inkMute, fontStyle: 'normal' }}>Loading clips…</span>
        </div>
      )}

      {error && !loading && (
        <div style={distStyles.error}>
          <div style={{ color: T.error, fontFamily: T.font.mono, fontStyle: 'normal', marginBottom: 8 }}>
            Failed to load clips
          </div>
          <div style={{ color: T.inkSoft, fontSize: 13, fontFamily: T.font.mono }}>
            {error.message}
          </div>
        </div>
      )}

      {!loading && !error && clips && clips.length === 0 && (
        <div style={distStyles.empty}>
          <span style={{ color: T.inkMute, fontStyle: 'normal' }}>
            No clips {filter ? `under "${statusLabel(filter)}"` : 'yet'}.
          </span>
        </div>
      )}

      {!loading && !error && clips && clips.length > 0 && (
        <table style={distStyles.table}>
          <thead>
            <tr>
              <th style={distStyles.th}>Serial</th>
              <th style={distStyles.th}>Campaign</th>
              <th style={distStyles.th}>Platform</th>
              <th style={distStyles.thRight}>Views</th>
              <th style={distStyles.thRight}>Earnings</th>
              <th style={distStyles.th}>Status</th>
              <th style={distStyles.th}></th>
            </tr>
          </thead>
          <tbody>
            {clips.map((cl) => (
              <tr key={cl.id}>
                <td style={distStyles.td}>
                  <div style={distStyles.serial}>{cl.serial_code}</div>
                  {cl.submitted_url && (
                    <a
                      href={cl.submitted_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={distStyles.urlLink}
                    >
                      open ↗
                    </a>
                  )}
                </td>
                <td style={distStyles.td}>
                  <Link to={`/operator/campaigns/${cl.campaign_id}`} style={distStyles.campaignLink}>
                    {cl.campaign_title || '—'}
                  </Link>
                  {cl.campaign_artist && (
                    <div style={distStyles.campaignArtist}>{cl.campaign_artist}</div>
                  )}
                </td>
                <td style={distStyles.td}>
                  <span style={distStyles.platform}>{platformBadge(cl.platform)}</span>
                </td>
                <td style={distStyles.tdRight}>{formatNum(cl.verified_views)}</td>
                <td style={distStyles.tdRight}>{formatMoney(cl.earnings_cents)}</td>
                <td style={distStyles.td}>
                  <span style={{ color: statusColor(cl.status), fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    ● {statusLabel(cl.status)}
                  </span>
                  {cl.status === 'rejected' && cl.rejection_reason && (
                    <div style={distStyles.rejectReason}>{cl.rejection_reason}</div>
                  )}
                </td>
                <td style={distStyles.td}>
                  {cl.status === 'submitted' && (
                    <div style={distStyles.actions}>
                      <button
                        type="button"
                        disabled={busyId === cl.id}
                        onClick={() => handleVerify(cl.id, true)}
                        style={distStyles.actionApprove}
                      >
                        verify
                      </button>
                      <button
                        type="button"
                        disabled={busyId === cl.id}
                        onClick={() => handleVerify(cl.id, false)}
                        style={distStyles.actionReject}
                      >
                        reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

const distStyles: Record<string, React.CSSProperties> = {
  filters: {
    display: 'flex',
    gap: 8,
    marginBottom: 32,
    flexWrap: 'wrap' as const,
  },
  pill: {
    background: 'transparent',
    border: `1px solid ${T.rule}`,
    color: T.inkSoft,
    fontFamily: T.font.body,
    fontSize: 12,
    letterSpacing: '0.06em',
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, color 0.2s, background 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  pillActive: {
    borderColor: T.accent,
    color: T.ink,
    background: T.accentSoft,
  },
  pillCount: {
    fontFamily: T.font.mono,
    fontSize: 10,
    color: T.accent,
    opacity: 0.85,
  },
  empty: {
    padding: 64,
    textAlign: 'center' as const,
    border: `1px solid ${T.rule}`,
    fontSize: 14,
  },
  error: {
    padding: 32,
    border: `1px solid ${T.error}`,
    background: T.errorSoft,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 14,
  },
  th: {
    textAlign: 'left' as const,
    padding: '14px 16px 14px 0',
    borderBottom: `1px solid ${T.rule}`,
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
    fontWeight: 500,
  },
  thRight: {
    textAlign: 'right' as const,
    padding: '14px 0 14px 16px',
    borderBottom: `1px solid ${T.rule}`,
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
    fontWeight: 500,
  },
  td: {
    padding: '14px 16px 14px 0',
    borderBottom: `1px solid ${T.rule}`,
    color: T.ink,
    fontSize: 14,
    verticalAlign: 'top' as const,
  },
  tdRight: {
    padding: '14px 0 14px 16px',
    borderBottom: `1px solid ${T.rule}`,
    color: T.ink,
    fontSize: 14,
    textAlign: 'right' as const,
    fontFamily: T.font.mono,
  },
  serial: {
    fontFamily: T.font.mono,
    fontSize: 12,
    color: T.ink,
  },
  urlLink: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    color: T.accent,
    textDecoration: 'none',
    borderBottom: `1px solid ${T.accent}`,
    paddingBottom: 1,
  },
  campaignLink: {
    fontFamily: T.font.body,
    fontSize: 14,
    color: T.ink,
    textDecoration: 'none',
    borderBottom: `1px solid transparent`,
    transition: 'border-color 0.2s',
  },
  campaignArtist: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.accent,
    marginTop: 2,
  },
  platform: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.inkSoft,
    letterSpacing: '0.04em',
  },
  rejectReason: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    color: T.inkMute,
    marginTop: 4,
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  actionApprove: {
    background: 'transparent',
    border: `1px solid ${T.live}`,
    color: T.live,
    fontFamily: T.font.body,
    fontSize: 11,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    padding: '6px 12px',
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
  },
  actionReject: {
    background: 'transparent',
    border: `1px solid ${T.error}`,
    color: T.error,
    fontFamily: T.font.body,
    fontSize: 11,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    padding: '6px 12px',
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
  },
};
